import React, { useState } from 'react';
import 'twin.macro';
import SimpleNarrowPage from 'components/Layouts/SimpleNarrowPage';
import { useAllEDTs } from 'queries/edt/useAllEDTs';
import _orderBy from 'lodash-es/orderBy';
import { BadgeColor } from '@teetsh/app/src/types/colors';
import { Tag } from 'components/Tag';
import { FilterBar } from 'components/FilterBar';
import _uniq from 'utils/uniq';
import ErrorFallback from 'components/Errors/ErrorFallback';
import { ChevronRightIcon as ChevronRight } from '@heroicons/react/24/outline';
import { useSchoolGoTo } from 'containers/App/useSchoolGoTo';
import { useTranslation } from 'react-i18next';
import { Spinner } from 'components/Spinner';
import { OnboardingModulePageWrapper } from '@teetsh/app/src/components/OnboardingModulePageWrapper';

export function FromOld() {
  const { t } = useTranslation(['emploiDuTemps']);
  const [filter, setFilter] = useState<number | undefined>(undefined);
  const goTo = useSchoolGoTo();
  const { data, isLoading, error } = useAllEDTs();
  const yearToBadgeColor: Record<string, BadgeColor> = {};

  const getBadgeColor = (year: number): BadgeColor => {
    // Create an array of available badge colors
    const availableBadgeColors: BadgeColor[] = [
      'red',
      'blue',
      'green',
      'yellow',
      'indigo',
    ];

    // If the color for this year is not assigned, assign one from the available colors
    if (!yearToBadgeColor[year]) {
      // Use the year as a seed to determine the color index
      const colorIndex = year % availableBadgeColors.length;
      yearToBadgeColor[year] = availableBadgeColors[colorIndex];
    }

    return yearToBadgeColor[year];
  };

  const allYears = _orderBy(_uniq((data || [])?.map(edt => edt.schoolyear)), [
    'desc',
  ]);

  const edts = _orderBy(
    filter ? (data || []).filter(edt => edt.schoolyear == filter) : data || [],
    ['schoolyear', 'schoolyearId', 'schoolId', 'name'],
    ['desc'],
  );
  if (isLoading) {
    return (
      <OnboardingModulePageWrapper
        title={t('emploiDuTemps:new.fromOldTitle')}
        onBack={() => goTo('emploi-du-temps/new')}
      >
        <Spinner tw="mt-8 text-brand-intense mx-auto" />
      </OnboardingModulePageWrapper>
    );
  }
  if (error) {
    return (
      <ErrorFallback
        error={error as any}
        subject="la récupération emploi du temps"
      />
    );
  }

  return (
    <OnboardingModulePageWrapper
      title={t('emploiDuTemps:new.fromOldTitle')}
      onBack={() => goTo('emploi-du-temps/new')}
    >
      <SimpleNarrowPage tw="mb-12">
        <FilterBar tw="mb-6 bg-neutral-supershy">
          <div tw="flex flex-wrap gap-x-2 gap-y-2">
            {_orderBy(allYears, [], ['desc']).map(year => (
              <Tag
                active={filter === year}
                key={year}
                onClick={() => {
                  if (filter === year) {
                    return setFilter(undefined);
                  }
                  setFilter(year);
                }}
                color={getBadgeColor(year)}
              >
                {year}
              </Tag>
            ))}
          </div>
        </FilterBar>
        <ul
          role="list"
          tw="divide-y divide-gray-100 overflow-hidden bg-white shadow-sm ring-1 ring-neutral-intense/5 sm:rounded-xl"
        >
          {edts.map(edt => (
            <li
              key={edt.id}
              tw="relative flex justify-between gap-x-6 px-4 py-5 hover:bg-neutral-white sm:px-6 cursor-pointer flex-wrap"
              onClick={() =>
                goTo(
                  `emploi-du-temps/new/preview-old?edt-id=${edt.id}&schoolyear-id=${edt.schoolyearId}&school-id=${edt.schoolId}`,
                )
              }
            >
              <div tw="flex min-w-0 gap-x-4">
                <div tw="min-w-0 flex-auto">
                  <p tw="text-sm font-semibold leading-6 text-neutral-intense">
                    {edt.name}
                  </p>
                  <p tw="mt-1 flex text-xs leading-5 text-neutral-quiet">
                    {t('emploiDuTemps:new.xActivities', {
                      count: edt.nbOfSeances,
                    })}
                  </p>
                </div>
              </div>
              <div tw="flex items-center gap-x-4 min-w-0 ml-auto">
                <div tw="hidden sm:flex sm:flex-col sm:items-end min-w-0">
                  <div tw="flex text-sm leading-6 gap-x-2 text-neutral-intense min-w-0">
                    <Tag tw="min-w-0 max-w-[18rem]">
                      <span tw="block truncate">{edt.schoolName}</span>
                    </Tag>
                    <Tag color={getBadgeColor(edt.schoolyear)} tw="shrink-0">
                      {edt.schoolyear}
                    </Tag>
                  </div>
                  <p tw="mt-1 text-xs leading-5 text-neutral-quiet">
                    <div tw="flex gap-x-1">
                      {(edt.classroomNames || []).map(classroomName => (
                        <div tw="text-muted" key={classroomName}>
                          {classroomName}
                        </div>
                      ))}
                    </div>
                  </p>
                </div>
                <ChevronRight
                  tw="h-5 w-5 flex-none text-neutral-quiet"
                  aria-hidden="true"
                />
              </div>
            </li>
          ))}
        </ul>
      </SimpleNarrowPage>
    </OnboardingModulePageWrapper>
  );
}
